/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/A3/utils/a3EmbodiedCarbonFromFuels.ts.
 */

import type { FormDataResponse } from "../../../../../../interfaces/formInterfaces";
import type { CharacterizationFactorPublic } from "../../../../../../services/characterizationFactorsPublicService";
import {
  classifyFuelMixForA1EmbodiedEnergy,
  computeA1EmbodiedCarbonReferenceKgFromFuelRow,
  resolveA1UnitLabelFromValue,
} from "../../../../../../utils/a1FuelEmbodiedEnergy";
import { getFuelMixDropdownLabelForField } from "../../shared/fuelMixReferenceAutofill";
import { resolveA3CharacterizationFactorForMix } from "./a3CarbonCharacterizationFactor";
import { formatEmbodiedCarbonReferenceQty, formatEmbodiedCarbonFunctionalUnit } from "../../shared/embodiedCarbonReferenceQtyFormat";
import {
  discoverA3FuelFieldNames,
  type A3FuelFieldNameMap,
} from "./a3FuelMixAutofill";
import { computeA3EmbodiedEnergyMjForFuelFlatRow } from "./a3FuelEmbodiedEnergyKwh";
import {
  alignA3FuelsPerProcessRowCount,
  processFuelFlatStart,
  totalA3FuelFlatRows,
} from "./a3ProcessFuelLayout";

const FUEL_CODE = "A3_PROCESS_FUEL_DETAILS";
const CARBON_CODE = "A3_EMBODIED_CARBON_DETAILS";

function parseNum(raw: string | undefined): number {
  const n = Number(String(raw ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : NaN;
}

function fuelRowCalcExtras(
  values: Record<string, string>,
  fuelNames: A3FuelFieldNameMap,
  flatIndex: number,
  unitTokenById?: Record<string, string>
) {
  const qtyUnitField = fuelNames.quantityUnit ?? "fuelQuantity_unit_A3_01";
  const densityField = fuelNames.density ?? "fuelDensity_A3_01";
  const densityUnitField = fuelNames.densityUnit ?? "fuelDensity_unit_A3_01";
  const qtyUnitRaw =
    values[`${qtyUnitField}_${FUEL_CODE}_row${flatIndex}`] ?? "";
  const density = parseNum(
    values[`${densityField}_${FUEL_CODE}_row${flatIndex}`]
  );
  const densityUnitRaw =
    values[`${densityUnitField}_${FUEL_CODE}_row${flatIndex}`] ?? "";

  return {
    quantityUnitValue: qtyUnitRaw,
    quantityUnitLabel: resolveA1UnitLabelFromValue(
      qtyUnitRaw,
      [],
      unitTokenById
    ),
    fuelDensity: Number.isFinite(density) ? density : undefined,
    densityUnitLabel: resolveA1UnitLabelFromValue(
      densityUnitRaw,
      [],
      unitTokenById
    ),
  };
}

/**
 * Embodied carbon (kg CO₂eq) for one A3 manufacturing fuel row — same rules as Form A1.
 */
export function computeA3EmbodiedCarbonKgForFuelFlatRow(params: {
  formData: FormDataResponse;
  values: Record<string, string>;
  fuelNames: A3FuelFieldNameMap;
  flatIndex: number;
  characterizationFactors: CharacterizationFactorPublic[];
  unitTokenById?: Record<string, string>;
}): number | null {
  const {
    formData,
    values,
    fuelNames,
    flatIndex,
    characterizationFactors,
    unitTokenById,
  } = params;

  const mixField = fuelNames.mix;
  if (!mixField) return null;

  const mix = String(
    values[`${mixField}_${FUEL_CODE}_row${flatIndex}`] ?? ""
  ).trim();
  if (!mix) return null;

  const cf = resolveA3CharacterizationFactorForMix(mix, characterizationFactors);
  if (!cf || !Number.isFinite(cf.value) || cf.value <= 0) return null;

  const label = getFuelMixDropdownLabelForField(
    formData,
    FUEL_CODE,
    mixField,
    mix
  );
  const category = classifyFuelMixForA1EmbodiedEnergy(mix, label);
  const qField = fuelNames.quantity ?? "fuelQuantity_A3_01";
  const calField =
    fuelNames.calorific ?? "A3ManufacturingProcessFuelCalorificValue";
  const fuelQty = parseNum(
    values[`${qField}_${FUEL_CODE}_row${flatIndex}`]
  );
  const calorific = parseNum(
    values[`${calField}_${FUEL_CODE}_row${flatIndex}`]
  );
  const extras = fuelRowCalcExtras(values, fuelNames, flatIndex, unitTokenById);
  const energyMj = computeA3EmbodiedEnergyMjForFuelFlatRow(
    formData,
    values,
    fuelNames,
    flatIndex,
    unitTokenById
  );

  return computeA1EmbodiedCarbonReferenceKgFromFuelRow({
    category,
    fuelQty,
    calorificValueMjPerKg: calorific,
    cfValue: cf.value,
    cfUnitLabel: cf.unitLabel,
    energyMj: energyMj ?? undefined,
    extras,
  });
}

/** Type definition — A3Embodied Carbon A1Field Names. */
export type A3EmbodiedCarbonA1FieldNames = {
  functionalField: string;
  rawMaterialQtyField: string;
  referenceEmbodiedField: string | null;
  referenceEmbodiedUnitField: string | null;
  functionalUnitField: string | null;
};

/** Exported function — discover A3Carbon Functional And Raw Material Qty Fields. */
export function discoverA3CarbonFunctionalAndRawMaterialQtyFields(
  formData: FormDataResponse | null
): A3EmbodiedCarbonA1FieldNames | null {
  const sec = formData?.formSubSections?.find((s) => s.code === CARBON_CODE);
  if (!sec?.fields?.length) return null;
  const L = (x?: string) => (x ?? "").toLowerCase();

  const functionalField = sec.fields.find(
    (f) =>
      f.type !== "UNIT" &&
      L(f.name).includes("functional") &&
      !L(f.name).includes("unit")
  )?.name;

  const rawMaterialQtyField = sec.fields.find(
    (f) =>
      f.type !== "UNIT" &&
      ((L(f.name).includes("rawmaterial") && L(f.name).includes("qty")) ||
        (L(f.name).includes("reference") && L(f.name).includes("qty")))
  )?.name;

  const rawFallback =
    rawMaterialQtyField ?? "A3EmbodiedCarbonDetailsRawMaterialQty";
  const rawLower = L(rawFallback);

  const referenceEmbodiedField =
    sec.fields.find((f) => {
      if (f.type === "UNIT") return false;
      const n = L(f.name);
      if (n === rawLower) return false;
      if (n.includes("rawmaterial") && n.includes("qty")) return false;
      if (n.includes("functional")) return false;
      if (n.includes("characterization")) return false;
      if (!n.includes("reference")) return false;
      if (n.includes("unit")) return false;
      return true;
    })?.name ?? null;

  const referenceEmbodiedUnitField = referenceEmbodiedField
    ? sec.fields.find(
        (f) =>
          f.type === "UNIT" &&
          (L(f.name).includes("reference") ||
            L(f.label ?? "").includes("reference")) &&
          !L(f.name).includes("functional")
      )?.name ?? null
    : null;

  const functionalUnitField =
    sec.fields.find(
      (f) => f.type === "UNIT" && L(f.name).includes("functional")
    )?.name ?? null;

  return {
    functionalField: functionalField ?? "A3EmbodiedCarbonDetailsFunctional",
    rawMaterialQtyField: rawFallback,
    referenceEmbodiedField,
    referenceEmbodiedUnitField,
    functionalUnitField,
  };
}

/** Exported function — discover A3Carbon Functional Unit Field. */
export function discoverA3CarbonFunctionalUnitField(
  formData: FormDataResponse | null
): string {
  const sec = formData?.formSubSections?.find((s) => s.code === CARBON_CODE);
  if (!sec?.fields?.length) {
    return "A3EmbodiedCarbonDetailsFunctionalUnit_A3_01";
  }
  const L = (x?: string) => (x ?? "").toLowerCase();
  const u = sec.fields.find(
    (f) => f.type === "UNIT" && L(f.name).includes("functional")
  )?.name;
  return u ?? "A3EmbodiedCarbonDetailsFunctionalUnit_A3_01";
}

type ReferenceCarbonParams = {
  formData: FormDataResponse | null;
  values: Record<string, string>;
  fuelsPerProcess: number[];
  fuelNames?: A3FuelFieldNameMap | null;
  characterizationFactors: CharacterizationFactorPublic[];
  unitTokenById?: Record<string, string>;
};

/**
 * Σ embodied carbon (kg CO₂eq) for fuel slots belonging to one manufacturing sub-process.
 */
export function computeA3ReferenceEmbodiedCarbonA1SimpleForProcess(
  params: ReferenceCarbonParams,
  processIndex: number,
  processRowCount: number
): number {
  const { formData, values, fuelsPerProcess, characterizationFactors } =
    params;

  if (!formData || processRowCount <= 0 || processIndex < 0) return 0;
  const fuelNames = params.fuelNames ?? discoverA3FuelFieldNames(formData);
  if (!fuelNames.mix) return 0;

  const counts = alignA3FuelsPerProcessRowCount(
    fuelsPerProcess,
    processRowCount
  );
  const start = processFuelFlatStart(processIndex, counts);
  const nFuels = Math.max(1, counts[processIndex] ?? 1);

  let sum = 0;
  for (let j = 0; j < nFuels; j++) {
    const flat = start + j;
    const kg = computeA3EmbodiedCarbonKgForFuelFlatRow({
      formData,
      values,
      fuelNames,
      flatIndex: flat,
      characterizationFactors,
      unitTokenById: params.unitTokenById,
    });
    if (kg != null && Number.isFinite(kg)) sum += kg;
  }
  return Math.round(sum * 1e6) / 1e6;
}

/** Cumulative embodied carbon (kg CO₂eq) — sum over all A3 manufacturing fuel rows. */
export function computeA3TotalEmbodiedCarbonKgCo2e(params: {
  formData: FormDataResponse | null;
  values: Record<string, string>;
  fuelsPerProcess: number[];
  fuelNames?: A3FuelFieldNameMap | null;
  characterizationFactors: CharacterizationFactorPublic[];
  unitTokenById?: Record<string, string>;
}): number {
  const {
    formData,
    values,
    fuelsPerProcess,
    characterizationFactors,
    unitTokenById,
  } = params;

  if (!formData) return 0;
  const fuelNames = params.fuelNames ?? discoverA3FuelFieldNames(formData);
  if (!fuelNames.mix) return 0;

  const n = totalA3FuelFlatRows(fuelsPerProcess);
  let sum = 0;
  for (let flat = 0; flat < n; flat++) {
    const kg = computeA3EmbodiedCarbonKgForFuelFlatRow({
      formData,
      values,
      fuelNames,
      flatIndex: flat,
      characterizationFactors,
      unitTokenById,
    });
    if (kg != null && Number.isFinite(kg)) sum += kg;
  }
  return Math.round(sum * 1000) / 1000;
}

/**
 * Auto-fill embodied carbon reference / functional unit per sub-process row.
 */
export function buildA3EmbodiedCarbonFunctionalValueUpdates(
  formData: FormDataResponse | null,
  values: Record<string, string>,
  processRowCount: number,
  fuelsPerProcess: number[],
  characterizationFactors: CharacterizationFactorPublic[],
  manualByRow: Record<string, boolean>,
  options?: {
    unitTokenById?: Record<string, string>;
    kgCo2eUnitId?: string;
    functionalCarbonUnitValue?: string;
  }
): Record<string, string> {
  if (!formData || processRowCount <= 0) return {};

  const fields = discoverA3CarbonFunctionalAndRawMaterialQtyFields(formData);
  if (!fields) return {};

  const updates: Record<string, string> = {};
  const P = Math.max(1, processRowCount);
  const kgCo2eUnitId = options?.kgCo2eUnitId?.trim() ?? "";
  const fnUnitToken =
    String(options?.functionalCarbonUnitValue ?? "").trim() || kgCo2eUnitId;

  for (let p = 0; p < P; p++) {
    const rowKey = `row${p}`;
    if (manualByRow[rowKey]) continue;

    const referenceA1 = computeA3ReferenceEmbodiedCarbonA1SimpleForProcess(
      {
        formData,
        values,
        fuelsPerProcess,
        characterizationFactors,
        unitTokenById: options?.unitTokenById,
      },
      p,
      P
    );

    const rawKey = `${fields.rawMaterialQtyField}_${CARBON_CODE}_row${p}`;
    const fnKey = `${fields.functionalField}_${CARBON_CODE}_row${p}`;
    const refKey = fields.referenceEmbodiedField
      ? `${fields.referenceEmbodiedField}_${CARBON_CODE}_row${p}`
      : null;
    const refUnitKey = fields.referenceEmbodiedUnitField
      ? `${fields.referenceEmbodiedUnitField}_${CARBON_CODE}_row${p}`
      : null;
    const fnUnitKey = fields.functionalUnitField
      ? `${fields.functionalUnitField}_${CARBON_CODE}_row${p}`
      : null;

    const rawParsed = Number(
      String(values[rawKey] ?? "")
        .replace(/,/g, "")
        .trim()
    );

    const refStr =
      Number.isFinite(referenceA1) && referenceA1 > 0
        ? formatEmbodiedCarbonReferenceQty(referenceA1)
        : "";

    if (refKey && values[refKey] !== refStr) {
      updates[refKey] = refStr;
    }

    if (kgCo2eUnitId && refUnitKey && values[refUnitKey] !== kgCo2eUnitId) {
      updates[refUnitKey] = kgCo2eUnitId;
    }

    if (!Number.isFinite(referenceA1) || referenceA1 <= 0) {
      if (String(values[fnKey] ?? "").trim() !== "") {
        updates[fnKey] = "";
      }
      continue;
    }

    if (!Number.isFinite(rawParsed) || rawParsed <= 0) {
      if (String(values[fnKey] ?? "").trim() !== "") {
        updates[fnKey] = "";
      }
      continue;
    }

    const fnStr = formatEmbodiedCarbonFunctionalUnit(referenceA1 / rawParsed);
    if (values[fnKey] !== fnStr) {
      updates[fnKey] = fnStr;
    }
    if (fnUnitToken && fnUnitKey && values[fnUnitKey] !== fnUnitToken) {
      updates[fnUnitKey] = fnUnitToken;
    }
  }

  return updates;
}
